import { AutoComplete, ISelectType, useFormContext } from "@4uhub/lib4uhub";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useTemplateWhats } from "../../../store/contexts/TemplateWhatsContext";
import { IWhatsappTemplateActionButtonProps } from "./models";
import { isReagendamentoAutomationCode } from "../../../utils/ticketAutomation";

const translationPath = "chatBot.page.templates.whatsapp_template.";

export const WhatsappTemplateActionButton = memo(
  ({ setIsNone }: IWhatsappTemplateActionButtonProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down("sm"));

    const {
      actionTypes,
      fixDataHandler,
      reorderCtaButtons,
      setCustomButtonsArray,
    } = useTemplateWhats();

    const { control, setValue, getValues } = useFormContext<any>();

    const clearCtas = useCallback(() => {
      setValue("ctaButtons", [], { shouldDirty: true, shouldValidate: true });
      reorderCtaButtons([]);
    }, [setValue, reorderCtaButtons]);

    const clearCustoms = useCallback(() => {
      setValue("customButtons", [], {
        shouldDirty: true,
        shouldValidate: true,
      });
      setCustomButtonsArray([]);
    }, [setValue, setCustomButtonsArray]);

    const ticketAutomation = useWatch({
      control,
      name: "ticketAutomation",
    }) as { id?: string; code?: string | null } | null;

    const handleActionTypeChange = useCallback(
      (sel: ISelectType | null) => {
        if (!sel) return;

        const code = typeof sel.code === "string" ? sel.code : String(sel.code);

        if (code === "1") {
          clearCustoms();
          clearCtas();
          fixDataHandler(false);
          setIsNone(true);
          return;
        }

        if (code === "2") {
          clearCtas();
          setCustomButtonsArray([
            { id: "1", text: "Sim", order: 0 },
            { id: "2", text: "Não", order: 1 },
          ]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (code === "3") {
          clearCtas();
          setCustomButtonsArray([
            { id: "1", text: "Ok", order: 0 },
            { id: "2", text: "Cancelar", order: 1 },
          ]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (code === "4") {
          clearCtas();
          setCustomButtonsArray([
            { id: "1", text: "Ok", order: 0 },
            { id: "2", text: "Cancelar", order: 1 },
            { id: "3", text: "Não Sou Eu", order: 2 },
          ]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (code === "6") {
          clearCtas();
          setCustomButtonsArray([
            { id: "1", text: "Confirmar", order: 0 },
            { id: "2", text: "Cancelar", order: 1 },
            { id: "3", text: "Não Sou Eu", order: 2 },
          ]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (code === "7") {
          clearCtas();
          setCustomButtonsArray([
            { id: "1", text: "Confirmar", order: 0 },
            { id: "2", text: "Cancelar", order: 1 },
          ]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (code === "8") {
          clearCtas();
          setCustomButtonsArray([
            { id: "1", text: "Ok", order: 0 },
            { id: "2", text: "Não Sou Eu", order: 1 },
          ]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (code === "9") {
          clearCustoms();
          const btn = {
            actionType: {
              id: "2",
              name: "components.whatsButtonsOptions.access_site.name",
              code: "2",
              description:
                "components.whatsButtonsOptions.access_site.description",
              value: "URL",
            },
            order: 1,
            urlType: {
              id: "2",
              name: t(translationPath + "dynamic"),
            },
            id: new Date().toISOString(),
            text: "",
            payload: "",
          };
          setValue("ctaButtons", [btn], {
            shouldDirty: true,
            shouldValidate: true,
          });
          reorderCtaButtons([btn as any]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (code === "10") {
          clearCtas();
          setCustomButtonsArray([{ id: "1", text: "Ok", order: 0 }]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (code === "11") {
          clearCtas();
          setCustomButtonsArray([
            { id: "1", text: "Ok", order: 0 },
            { id: "2", text: "Falar com Atendente", order: 1 },
          ]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        if (
          code === "12" &&
          isReagendamentoAutomationCode(ticketAutomation?.code)
        ) {
          clearCtas();
          setCustomButtonsArray([
            { id: "1", text: "Sim, quero agendar", order: 0 },
            { id: "2", text: "Não quero reagendar", order: 1 },
          ]);
          fixDataHandler(true);
          setIsNone(false);
          return;
        }

        setIsNone(false);
      },
      [
        clearCtas,
        clearCustoms,
        ticketAutomation?.code,
        setCustomButtonsArray,
        setValue,
        reorderCtaButtons,
        fixDataHandler,
        setIsNone,
      ]
    );

    useEffect(() => {
      if (!ticketAutomation?.id) return;

      clearCtas();
      clearCustoms();

      setValue("actionType", null, {
        shouldDirty: false,
        shouldValidate: false,
        shouldTouch: false,
      });

      fixDataHandler(false);
      setIsNone(true);
    }, [
      ticketAutomation?.id,
      clearCtas,
      clearCustoms,
      setValue,
      fixDataHandler,
      setIsNone,
    ]);

    useEffect(() => {
      if (!ticketAutomation?.id) return;
      if (!Array.isArray(actionTypes) || actionTypes.length !== 1) return;

      const only = actionTypes[0] as ISelectType;
      const current = getValues("actionType") as ISelectType | null;

      if (
        !current ||
        current.id !== only.id ||
        current.code !== only.code
      ) {
        setValue("actionType", only, {
          shouldDirty: false,
          shouldValidate: true,
          shouldTouch: false,
        });
        handleActionTypeChange(only);
      }
    }, [
      ticketAutomation?.id,
      actionTypes,
      getValues,
      setValue,
      handleActionTypeChange,
    ]);

    return (
      <Grid item xs={12} sm={6}>
        <AutoComplete
          sx={{ width: matches ? "100%" : 300 }}
          name="actionType"
          options={actionTypes}
          onValueChange={handleActionTypeChange}
          label={t(translationPath + "action_type")}
          getOptionLabel={(option) => option.name}
          size="small"
          fullWidth
          clearIcon={false}
          disable={!ticketAutomation}
        />
      </Grid>
    );
  }
);
